import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ApplicationService } from 'src/app/services/application.service';
import { PrintService } from 'src/app/services/print.service';
import { ModalService } from 'src/app/services/modal.service';
import { ApplicationData } from 'src/app/data/application.data';
import { DragulaService } from 'ng2-dragula';
import { Subscription, Observable } from 'rxjs';
import { TextLayerData } from 'src/app/data/configurator/layer/text-layer.data';
import { HistoryData } from 'src/app/data/configurator/history.data';
import { CommandData } from 'src/app/data/configurator/command.data';
import { LabelData } from 'src/app/data/configurator/label/label.data';
import { BaseToolComponent } from 'src/app/components/configurator/base-tool.component';
import { TintableLayerData } from 'src/app/data/configurator/layer/tintable-layer.data';
import { CONFIGURATION_MODE } from 'src/app/data/configuration.data';
import { LAYER_TYPE, LayerData } from 'src/app/data/configurator/layer/layer.data';
import { ContextData } from 'src/app/data/context.data';
import { TextLayer } from 'src/app/display/text-layer';
import { CoordUtil } from 'src/app/tools/coord.utils';

declare const fabric: any;

@Component({
  selector: 'excel-panel',
  templateUrl: './excel-panel.component.html',
  host: {
    class: 'excel-panel'
  }
})
export class ExcelPanelComponent extends BaseToolComponent implements OnInit, OnDestroy {

  /**
	 * Constructor
	 */

  private _dragulaSub:Subscription;
  private _mouseX:number;
  private _mouseY:number;
  public progress:string = "0/0";

  @Input() set label(data: LabelData) {
    if ((this._labelData && this._labelData != data) || !this._labelData) {
      this.clearCurrentLabel();
      this._labelData = data;
      this.setLabel();
    }
  }


  constructor(
    private modalService: ModalService,
    public applicationService:ApplicationService,
    public applicationData:ApplicationData,
    private dragulaService: DragulaService
  ) {
      super();
      dragulaService.createGroup('EXCELFIELD', {
        copy: (el, source) => {
          return true;
        },
        accepts: (el, target, source, sibling) => {
          return false;
        }
      });

      this._dragulaSub = new Subscription();

      this._dragulaSub.add(dragulaService.drag('EXCELFIELD').subscribe(() => {
        document.addEventListener('mousemove', this.onMouseMove);
        // console.log("DRAG");
      }));

      this._dragulaSub.add(dragulaService.drop('EXCELFIELD').subscribe(({name, el, target, source, sibling}) => {

        document.removeEventListener('mousemove', this.onMouseMove);
        if(this.applicationData.configuration.currentLabelScreenRect.contains(this._mouseX, this._mouseY)){
          this.addExcelTextLayer(parseInt(el.id.substr(11)))
        }
      }));

  }

  /**
	 * addExcelTextLayer
	 */
  public addExcelTextLayer(excelColumnIndex:number) {
    var layerData: TextLayerData = new TextLayerData(this.applicationData.configuration.csv[1][excelColumnIndex], TextLayerData.DEFAULT_FONT, TextLayerData.DEFAULT_SIZE, TintableLayerData.DEFAULT_COLOR, TextLayerData.DEFAULT_BOLD, TextLayerData.DEFAULT_ITALIC, TextLayerData.DEFAULT_UNDERLINE, TextLayerData.DEFAULT_ALIGN, 0);
    layerData.source = LayerData.EXCEL;
    layerData.special = excelColumnIndex.toString();
    layerData.rotation = this.applicationData.configuration.currentCanvasRotation.valueOf()*-1;
    this._labelData.addLayer(layerData);

    HistoryData.add(this._labelData, layerData, CommandData.REMOVE_LAYER);

    this.applicationService.currentLayerData = layerData;
    layerData.invalidate();
  }

  onMouseMove = (e:MouseEvent) => {
    this._mouseX = e.pageX;
    this._mouseY = e.pageY;
  }

	/**
	 * On init
	 */
  ngOnInit() {
    //console.log(this.applicationData.configuration.csv);
  }

  process(){
     this.modalService.open('modal-confirm-excel-process', {});
  }

  processConfirm(){
    this.modalService.cancel('modal-confirm-excel-process');

    this.progress = "0/"+(this.applicationData.configuration.csv.length-1);

    this.modalService.open('modal-excel-generation', {});

    this.initCanvasForPositioningCorrection();

    this.startExcelGeneration().subscribe({
			next: (progress) => {
        this.progress = progress;
			},
			error: (error) => {
			  console.error(error);
        this.cleanCanvasForPositioningCorrection();
			},
			complete: () => {
        this.progress = (this.applicationData.configuration.csv.length-1)+"/"+(this.applicationData.configuration.csv.length-1);
        this.modalService.cancel('modal-excel-generation');
        this.applicationData.configuration.mode = CONFIGURATION_MODE.NORMAL;
        this.cleanCanvasForPositioningCorrection();
			}
		});
  }

  private canvasForPositioningCorrection;

  initCanvasForPositioningCorrection(){
    this.canvasForPositioningCorrection = new fabric.StaticCanvas(document.createElement("canvas"));
  }

  recomputeTextLayerData(originalText:string, textData:TextLayerData){
    // console.log("recomputeTextLayerData()",originalText, textData);
    //var text = new TextLayer(textData, this.canvasForPositioningCorrection);
    var text = new fabric.IText(originalText, {
      fontSize: 100,
      top: textData.y,
      left: textData.x,
      fill: textData.color,
      width: textData.width,
      fontFamily: textData.font,
      fontWeight: textData.bold ? 'bold' : 'normal',
      fontStyle: textData.italic ? 'italic' : 'normal',
      underline: textData.underline,
      textAlign: textData.align,
      scaleX: textData.size * ContextData.POINTS_TO_PIXELS / 100,
      scaleY: textData.size * ContextData.POINTS_TO_PIXELS / 100,
      lineHeight: Math.max(textData.leading / 25 + 1, 0.0001)
    });
    this.canvasForPositioningCorrection.add(text);
    let _originalBoundW:any = text.width;
    text.set('text', textData.text);
    let _newBoundW:any = text.width;

    let _deltaW = _newBoundW-_originalBoundW;
    let _offset = CoordUtil.rotate2D({x:_deltaW,y:0},textData.rotation);

    switch (textData.align) {
      case 'left':
        // nothing to do :)
        break;
      case 'center':
        textData.x -= _offset.x*text.scaleX*.5;
        textData.y -= _offset.y*text.scaleX*.5;
        break;
      case 'right':
        textData.x -= _offset.x*text.scaleX;
        textData.y -= _offset.y*text.scaleX;
        break;

      default:
        break;
    }

    textData.width = text.width * (textData.size * ContextData.POINTS_TO_PIXELS / 100);
    textData.height = text.height * (textData.size * ContextData.POINTS_TO_PIXELS / 100);

  }

  cleanCanvasForPositioningCorrection(){
    if(!this.canvasForPositioningCorrection)return;
    this.canvasForPositioningCorrection.dispose();
    this.canvasForPositioningCorrection = null;
  }

  startExcelGeneration():Observable<any> {

    return new Observable((observer) => {

      let _lastLabelIndex:number = this.applicationData.configuration.csv.length - 2; // (header + 0 start... We also regenerate 1st label)
      let _currentLabel:number = 0;

      let _firstCsvTextes:any = this.applicationData.configuration.csv[1];


      let _generateNextLabel = () => {
        setTimeout(() => {
          this.applicationData.configuration.addLabel().then((result: any) => {
              let _newLabel:LabelData = this.applicationData.configuration.labels[_currentLabel+1];
              _newLabel.removeAllLayers();
              _newLabel.layers = this.applicationData.configuration.labels[0].cloneLayers().map(layer=>{
                if(layer.source == LayerData.EXCEL){
                  if(!this.applicationData.configuration.csv[_currentLabel+1][parseInt(layer.special)]) return null;
                  let _text = (this.applicationData.configuration.csv[_currentLabel+1][parseInt(layer.special)]).toString();
                  if(_text.trim() == "") return null;
                  (layer as TextLayerData).text = _text;
                  layer.source = LayerData.CREATION;
                  this.recomputeTextLayerData(_firstCsvTextes[parseInt(layer.special)], layer as TextLayerData);
                  layer.special = null;
                }
                return layer;
              }).filter((layer:any)=>{
                return !!layer;
              });

              _currentLabel++;
              observer.next(_currentLabel+"/"+(_lastLabelIndex+1));

              if(_currentLabel <= _lastLabelIndex){
                _generateNextLabel();
              } else {

                // On suppr l'original (a été recréé)
                this.applicationData.configuration.deleteLabel(this.applicationData.configuration.labels[0]);

                observer.complete();
              }

            },
            (error: any) => {
              observer.error(error);
            },
          ).catch((e: Error) => { observer.error(e); });
        });

      };

      _generateNextLabel();


    });
  }

	/**
	* On destroy
	*/
  ngOnDestroy() {
    this._dragulaSub.unsubscribe();
    this._dragulaSub = null;
    this.dragulaService.destroy('EXCELFIELD');
  }
}
